import { useEffect, useRef, useState, type ReactNode } from 'react';

// Marks a report section ready shortly before it enters the viewport. Once a
// section is ready it stays ready, so changing its filters continues to refresh
// normally without re-observing the DOM node.
export function useLazySection(enabled: boolean, rootMargin = '480px') {
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled || ready) return;
    if (typeof IntersectionObserver === 'undefined') {
      setReady(true);
      return;
    }
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setReady(true);
      observer.disconnect();
    }, { rootMargin });
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, ready, rootMargin]);

  return { ref, ready };
}

// Defers mounting an expensive report altogether. This is useful for pages
// composed of several independent report tables: an off-screen table must not
// issue a request merely because its parent page mounted.
export function LazyReportSection({
  children,
  enabled,
  minHeight = 300,
}: {
  children: ReactNode;
  enabled: boolean;
  minHeight?: number;
}) {
  const { ref, ready } = useLazySection(enabled);

  return (
    <div ref={ref} style={!ready ? { minHeight } : undefined}>
      {ready ? children : (
        <div className="card" style={{ minHeight, display: 'grid', placeItems: 'center', color: 'var(--text3)', fontSize: 13 }}>
          滚动到此报表后加载
        </div>
      )}
    </div>
  );
}
